<template>
  <main class="sf-page">
    <div class="sf-loader" v-if="loading"><span class="sf-loader__ring"></span></div>
    <div v-else-if="error" class="sf-section sf-section--cream">
      <div class="sf-container sf-error">
        <div class="sf-error__icon-wrap"><X /></div>
        <h2 class="sf-h2">Article non trouvé</h2>
        <router-link to="/actualites"><button class="sf-btn sf-btn--terra"><ArrowLeft class="sf-btn__icon" />Retour</button></router-link>
      </div>
    </div>

    <template v-else-if="article">
      <!-- Article hero -->
      <div class="sn-hero">
        <img v-if="article.featured_image" :src="imageUrl+article.featured_image" :alt="article.title" loading="lazy" class="sn-hero__img" />
        <div v-else class="sn-hero__fallback"><Calendar /></div>
        <div class="sn-hero__overlay"></div>
        <div class="sn-hero__content">
          <div class="sn-hero__cats">
            <span class="sf-card__cat" style="position:static">{{ article.category }}</span>
            <span v-if="article.is_featured" class="sf-card__badge" style="position:static">À la une</span>
          </div>
          <h1 class="sn-hero__title">{{ article.title }}</h1>
          <div class="sn-hero__meta">
            <span class="sf-chip" style="color:rgba(245,239,224,.75)"><Calendar />{{ formatDate(article.published_at) }}</span>
            <span class="sf-chip" style="color:rgba(245,239,224,.75)"><Clock />{{ article.reading_time_minutes||5 }} min</span>
            <span class="sf-chip" style="color:rgba(245,239,224,.75)"><User />{{ article.author?.name||'Admin' }}</span>
            <span class="sf-chip" style="color:rgba(245,239,224,.75)"><Eye />{{ formatNumber(article.views_count) }} vues</span>
          </div>
        </div>
      </div>

      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <div class="sn-layout">
            <!-- Article body -->
            <article class="sn-article">
              <router-link to="/actualites" class="sf-back-link"><ArrowLeft />Retour aux actualités</router-link>

              <!-- ── Extrait / Chapeau ─────────────────────────── -->
              <p v-if="article.excerpt" class="sn-excerpt">{{ article.excerpt }}</p>

              <div v-if="JSON.parse(article.tags)?.length" class="sn-tags" style="margin-top:1.25rem">
                <span v-for="tag in JSON.parse(article.tags)" :key="tag" class="sf-tag" @click="filterByTag(tag)">#{{ tag }}</span>
              </div>
              <button @click="shareArticle" class="sf-btn sf-btn--outline sf-btn--sm" style="margin-top:1rem"><Share2 class="sf-btn__icon" />Partager</button>

              <!-- Gallery -->
              <div v-if="article.gallery_images?.length" style="margin:1.5rem 0">
                <div class="sf-gallery">
                  <div v-for="(img,i) in article.gallery_images" :key="i" class="sf-gallery-item" style="height:140px" @click="openGallery(i)">
                    <img :src="imageUrl+img" loading="lazy" :alt="`Galerie ${i+1}`" />
                    <div class="sf-gallery-item__overlay"><ZoomIn /></div>
                  </div>
                </div>
              </div>

              <!-- Prose content -->
              <div class="sn-prose" v-html="formattedContent"></div>

              <div class="sn-signature">Publié le {{ formatDate(article.published_at) }} par {{ article.author?.name||'Admin' }}</div>

              <!-- CTA inline -->
              <div class="sn-cta-row">
                <div class="sn-cta-card sn-cta-card--terra">
                  <h3>Soutenez nos projets</h3>
                  <p>Votre don aide à installer des antennes et former des jeunes.</p>
                  <router-link to="/donate"><button class="sf-btn sf-btn--terra sf-btn--full sf-btn--sm">Faire un don</button></router-link>
                </div>
                <div class="sn-cta-card">
                  <h3>Restez informé</h3>
                  <p>Suivez nos actualités et découvrez toutes nos initiatives.</p>
                  <router-link to="/actualites"><button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm">Voir les actualités</button></router-link>
                </div>
              </div>

              <!-- Similar -->
              <div v-if="similarArticles.length" style="margin-top:3rem;padding-top:2rem;border-top:1px solid var(--ink-20)">
                <span class="sf-label">Articles similaires</span>
                <div class="sn-similar-grid">
                  <router-link v-for="sim in similarArticles" :key="sim.id" :to="`/actualite/${sim.slug}`" class="sn-similar-card">
                    <span style="font-size:.72rem;color:var(--terra);letter-spacing:.1em;text-transform:uppercase">{{ sim.category }}</span>
                    <p class="sn-similar-card__title">{{ sim.title }}</p>
                  </router-link>
                </div>
              </div>
            </article>

            <!-- Sidebar -->
            <aside class="sn-sidebar">
              <div class="sf-sidebar-card" style="position:sticky;top:80px">
                <div class="sf-sidebar-card__header"><h3>À propos</h3></div>
                <div class="sf-sidebar-card__body">
                  <p style="font-size:.84rem;color:var(--ink-60);line-height:1.6;margin-bottom:1rem">Stag Fondation agit chaque jour pour réduire la fracture numérique.</p>
                  <router-link to="/a-propos"><button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm">En savoir plus</button></router-link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <!-- Gallery modal -->
      <div v-if="showGalleryModal" class="sf-modal" @click.self="closeGallery">
        <button class="sf-modal__close" @click="closeGallery"><X /></button>
        <img :src="imageUrl+article.gallery_images[currentGalleryIndex]" style="max-height:80vh;object-fit:contain;margin:0 auto;display:block" />
        <p class="sf-modal__counter">{{ currentGalleryIndex+1 }} / {{ article.gallery_images.length }}</p>
      </div>
    </template>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { ArrowLeft, ArrowRight, Calendar, Clock, User, Eye, Share2, ZoomIn, X, ChevronLeft, ChevronRight } from 'lucide-vue-next'
export default {
  name:'NewsDetailPage',
  components:{ ArrowLeft, ArrowRight, Calendar, Clock, User, Eye, Share2, ZoomIn, X, ChevronLeft, ChevronRight },
  data(){return{showGalleryModal:false,currentGalleryIndex:0}},
  computed:{
    ...mapState({article:s=>s.currentArticle,loading:s=>s.loading,error:s=>s.error,imageUrl:s=>s.imageUrl,popularArticles:s=>s.popularArticles}),
    formattedContent(){
      if(!this.article?.content)return ''
      let c=this.article.content
      c=c.replace(/<h2/g,'<h2 class="sn-content-h2"')
      c=c.replace(/<h3/g,'<h3 class="sn-content-h3"')
      c=c.replace(/<p(?![ >])/g,'<p class="sn-content-p" ')
      c=c.replace(/<blockquote/g,'<blockquote class="sn-content-bq"')
      return c
    },
    similarArticles(){return this.popularArticles?.filter(a=>a.id!==this.article?.id).slice(0,3)||[]}
  },
  watch:{'$route.params.slug':{handler(s){if(s)this.loadArticle(s)},immediate:true}},
  mounted(){AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650})},
  methods:{
    ...mapActions(['fetchArticleBySlug','fetchPopularArticles','setFilter']),
    async loadArticle(s){try{await this.fetchArticleBySlug(s);await this.fetchPopularArticles()}catch(e){console.error(e)}},
    filterByTag(t){this.setFilter({key:'tag',value:t});this.$router.push('/actualites')},
    shareArticle(){if(navigator.share)navigator.share({title:this.article.title,url:window.location.href}).catch(()=>{});else navigator.clipboard.writeText(window.location.href).catch(()=>{})},
    openGallery(i){this.currentGalleryIndex=i;this.showGalleryModal=true},
    closeGallery(){this.showGalleryModal=false},
    formatDate(d){return d?new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'long',year:'numeric'}):''},
    formatNumber(n){return n>=1000?(n/1000).toFixed(1)+'k':String(n||0)}
  }
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
/* ── Hero ─────────────────────────────────────────────────── */
.sn-hero { position:relative; height:440px; overflow:hidden; background:var(--forest); }
.sn-hero__img { width:100%; height:100%; object-fit:cover; object-position:center 30%; }
.sn-hero__fallback { width:100%; height:100%; display:flex; align-items:center; justify-content:center; color:var(--sage); }
.sn-hero__fallback :deep(svg) { width:60px; height:60px; }
.sn-hero__overlay { position:absolute; inset:0; background:linear-gradient(to top, rgba(20,18,12,.88) 0%, rgba(20,18,12,.35) 60%, transparent 100%); }
.sn-hero__content { position:absolute; bottom:2.5rem; left:0; right:0; padding:0 2rem; max-width:1200px; margin:0 auto; }
.sn-hero__cats { display:flex; gap:.5rem; flex-wrap:wrap; margin-bottom:.75rem; }
.sn-hero__title {
  font-family:var(--fd); font-size:clamp(1.7rem,4.5vw,3.2rem);
  font-weight:600; color:var(--white); line-height:1.12;
  max-width:72ch; margin-bottom:1rem;
  /* Truncation si titre très long sur mobile */
  overflow-wrap:break-word; word-break:break-word;
}
.sn-hero__meta { display:flex; flex-wrap:wrap; gap:1.25rem; }

/* ── Layout ───────────────────────────────────────────────── */
.sn-layout { display:grid; grid-template-columns:1fr 270px; gap:3rem; align-items:start; }
@media (max-width:900px) { .sn-layout { grid-template-columns:1fr; } }

/* ── Extrait / Chapeau ────────────────────────────────────── */
/* Affiché immédiatement après le lien "Retour", avant le contenu */
.sn-excerpt {
  margin-top:1.5rem;
  padding:1.25rem 1.5rem;
  border-left:3px solid var(--terra);
  background:rgba(200,90,42,.05);
  border-radius:0 6px 6px 0;
  font-family:var(--fd);
  font-size:clamp(1rem,1.5vw,1.15rem);
  font-style:italic;
  color:var(--ink);
  line-height:1.7;
  overflow-wrap:break-word;
  word-break:break-word;
}

/* ── Tags ─────────────────────────────────────────────────── */
.sn-tags { display:flex; flex-wrap:wrap; gap:.35rem; }

/* ── Prose container ──────────────────────────────────────── */
.sn-prose {
  margin-top:1.75rem;
  line-height:1.8;
  /* Empêche tout débordement du contenu CMS */
  overflow-wrap:break-word;
  word-break:break-word;
  min-width:0; /* Indispensable dans un contexte grid/flex */
}

/* ── Éléments injectés via v-html ─────────────────────────── */

/* Texte générique */
:deep(.sn-prose) { overflow-wrap:break-word; word-break:break-word; }

/* Paragraphes */
:deep(.sn-content-p),
:deep(.sn-prose p) {
  font-size:.93rem; color:var(--ink-60); line-height:1.8;
  margin-bottom:1.35rem;
  overflow-wrap:break-word; word-break:break-word;
}

/* Titres */
:deep(.sn-content-h2),
:deep(.sn-prose h2) {
  font-family:var(--fd); font-size:clamp(1.4rem,3vw,1.85rem);
  font-weight:600; color:var(--ink); margin:2.75rem 0 .85rem;
  line-height:1.2; overflow-wrap:break-word; word-break:break-word;
}
:deep(.sn-content-h3),
:deep(.sn-prose h3) {
  font-family:var(--fd); font-size:clamp(1.15rem,2.5vw,1.4rem);
  font-weight:600; color:var(--ink); margin:2.25rem 0 .65rem;
  line-height:1.25; overflow-wrap:break-word; word-break:break-word;
}
:deep(.sn-prose h4) {
  font-weight:600; font-size:.95rem; color:var(--ink);
  margin:1.75rem 0 .5rem; overflow-wrap:break-word;
}

/* Citation */
:deep(.sn-content-bq),
:deep(.sn-prose blockquote) {
  border-left:3px solid var(--terra);
  padding:1rem 1.5rem;
  background:var(--cream-d);
  border-radius:0 6px 6px 0;
  margin:2rem 0;
  font-family:var(--fd); font-style:italic;
  font-size:1.05rem; color:var(--ink);
  line-height:1.7;
  overflow-wrap:break-word; word-break:break-word;
}

/* Images — elles ne doivent JAMAIS dépasser leur conteneur */
:deep(.sn-prose img),
:deep(.sn-prose figure img) {
  max-width:100%;
  height:auto;
  border-radius:6px;
  display:block;
  margin:2rem auto;
  box-shadow:var(--shadow-sm);
}
:deep(.sn-prose figure) {
  margin:2rem 0;
  overflow:hidden;
}
:deep(.sn-prose figcaption) {
  font-size:.78rem; color:var(--ink-60);
  text-align:center; margin-top:.5rem; font-style:italic;
}

/* Liens */
:deep(.sn-prose a) {
  color:var(--terra); text-decoration:underline;
  text-decoration-color:rgba(200,90,42,.3);
  overflow-wrap:break-word; word-break:break-all;
  transition:text-decoration-color .2s ease;
}
:deep(.sn-prose a:hover) { text-decoration-color:var(--terra); }

/* Listes */
:deep(.sn-prose ul),
:deep(.sn-prose ol) {
  padding-left:1.5rem; margin-bottom:1.35rem;
  color:var(--ink-60); font-size:.93rem; line-height:1.75;
}
:deep(.sn-prose li) { margin-bottom:.4rem; overflow-wrap:break-word; }
:deep(.sn-prose ul li)  { list-style:disc; }
:deep(.sn-prose ol li)  { list-style:decimal; }

/* Code & Pre — scrollable, jamais débordant */
:deep(.sn-prose code) {
  background:var(--cream-d); border-radius:3px;
  padding:.15rem .4rem; font-size:.82rem; font-family:monospace;
  color:var(--terra); word-break:break-all;
}
:deep(.sn-prose pre) {
  background:var(--ink); color:var(--cream);
  border-radius:6px; padding:1.25rem 1.5rem;
  overflow-x:auto; /* scroll horizontal → pas de débordement vertical */
  max-width:100%;
  margin:1.75rem 0;
  font-size:.82rem; line-height:1.65;
  white-space:pre; /* pre: ne pas casser les lignes de code */
}
:deep(.sn-prose pre code) {
  background:none; color:inherit;
  padding:0; font-size:inherit;
  word-break:normal;
}

/* Tableaux — scroll horizontal si trop larges */
:deep(.sn-prose table) {
  width:100%; border-collapse:collapse;
  font-size:.85rem; margin:2rem 0;
  display:block; overflow-x:auto; /* clé : scroll sur table large */
  max-width:100%;
}
:deep(.sn-prose th) {
  background:var(--forest); color:var(--white);
  padding:.65rem 1rem; text-align:left; font-weight:600;
  white-space:nowrap;
}
:deep(.sn-prose td) {
  padding:.6rem 1rem; border-bottom:1px solid var(--cream-d);
  color:var(--ink-60); vertical-align:top;
  overflow-wrap:break-word; word-break:break-word;
}
:deep(.sn-prose tr:hover td) { background:var(--cream); }

/* Séparateur */
:deep(.sn-prose hr) {
  border:none; border-top:1px solid var(--ink-20);
  margin:2.5rem 0;
}

/* Iframe / embed (vidéo) — responsive */
:deep(.sn-prose iframe),
:deep(.sn-prose video) {
  max-width:100%; border-radius:6px;
  display:block; margin:2rem auto;
}

/* ── Signature ───────────────────────────────────────────── */
.sn-signature {
  font-size:.8rem; color:var(--ink-60); font-style:italic;
  margin-top:2rem; padding-top:1.5rem;
  border-top:1px solid var(--ink-20);
}

/* ── CTA inline ──────────────────────────────────────────── */
.sn-cta-row { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-top:2rem; }
@media (max-width:540px) { .sn-cta-row { grid-template-columns:1fr; } }
.sn-cta-card { background:var(--white); border:1px solid var(--ink-20); border-radius:8px; padding:1.5rem; box-shadow:var(--shadow-sm); }
.sn-cta-card--terra { border-color:rgba(200,90,42,.2); background:rgba(200,90,42,.04); }
.sn-cta-card h3 { font-family:var(--fd); font-size:1.1rem; font-weight:600; color:var(--ink); margin-bottom:.4rem; }
.sn-cta-card p { font-size:.82rem; color:var(--ink-60); line-height:1.55; margin-bottom:1rem; }

/* ── Articles similaires ─────────────────────────────────── */
.sn-similar-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; margin-top:.75rem; }
.sn-similar-card { display:block; padding:1rem; background:var(--white); border:1px solid var(--ink-20); border-radius:6px; text-decoration:none; transition:border-color .2s ease, transform .2s ease; }
.sn-similar-card:hover { border-color:var(--terra); transform:translateY(-2px); }
.sn-similar-card__title {
  font-size:.88rem; font-weight:500; color:var(--ink);
  margin-top:.35rem; line-height:1.35;
  display:-webkit-box; -webkit-line-clamp:2;
  -webkit-box-orient:vertical; overflow:hidden;
  overflow-wrap:break-word; word-break:break-word;
}

/* ── Mobile ───────────────────────────────────────────────── */
@media (max-width:640px) {
  .sn-hero { height:360px; }
  .sn-hero__content { bottom:1.5rem; padding:0 1.25rem; }
  .sn-excerpt { font-size:.95rem; padding:1rem 1.1rem; }
}
</style>